'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import RestrictedState from '@/components/shared/RestrictedState';
import { Skeleton } from '@/components/shared/Skeletons';
import { mockNewsletterIssues, getIssueBySlug, NewsletterIssue } from '@/data/mockNewsletter';
import { ArrowLeft, Calendar, Clock } from 'lucide-react';

function IssueSkeleton() {
  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
      <Skeleton className="h-4 w-32 mb-8" />
      <Skeleton className="h-3 w-20 mb-3" />
      <Skeleton className="h-9 w-3/4 mb-2" />
      <Skeleton className="h-4 w-40 mb-8" />
      <div className="space-y-3">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-5/6" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-4/5" />
      </div>
    </div>
  );
}

function RelatedIssueCard({ issue }: { issue: NewsletterIssue }) {
  return (
    <Link href={`/newsletter/${issue.slug}`} className="block group focus-visible:outline-none">
      <div className="neon-card p-4 group-focus-visible:border-[rgba(76,201,255,0.35)]">
        <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#4CC9FF' }}>
          Issue #{issue.issueNumber}
        </span>
        <h4 className="font-semibold text-[13px] leading-snug mt-1 mb-1" style={{ color: '#F2F4FF' }}>
          {issue.title}
        </h4>
        <p className="text-[12px]" style={{ color: '#A7A3C4' }}>{issue.date}</p>
      </div>
    </Link>
  );
}

export default function NewsletterIssuePage() {
  const params = useParams();
  const slug = params?.issueSlug as string;
  const [isLoading, setIsLoading] = useState(true);
  const [issue, setIssue] = useState<NewsletterIssue | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      const found = getIssueBySlug(slug);
      if (found) {
        setIssue(found);
      } else {
        setNotFound(true);
      }
      setIsLoading(false);
    }, 600);
    return () => clearTimeout(timer);
  }, [slug]);

  const relatedIssues = mockNewsletterIssues
    .filter((i) => i.slug !== slug)
    .slice(0, 3);

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        {isLoading && <IssueSkeleton />}

        {!isLoading && notFound && (
          <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
            <Link href="/newsletter" className="inline-flex items-center gap-2 text-[13px] mb-8 transition-colors duration-150 hover:opacity-80" style={{ color: '#A7A3C4' }}>
              <ArrowLeft size={14} /> Back to Newsletter
            </Link>
            <RestrictedState
              variant="removed"
              title="Issue Unavailable"
              description="This newsletter issue has been removed or is no longer available. Browse the archive for other issues."
            />
          </div>
        )}

        {!isLoading && issue && (
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-10">
            <div className="max-w-3xl">
              {/* Back */}
              <Link href="/newsletter" className="inline-flex items-center gap-2 text-[13px] mb-8 transition-colors duration-150 hover:opacity-80" style={{ color: '#A7A3C4' }}>
                <ArrowLeft size={14} /> Back to Newsletter
              </Link>

              {/* Issue header */}
              <div
                className="rounded-xl p-6 sm:p-8 mb-8"
                style={{
                  background: 'linear-gradient(135deg, rgba(122,92,255,0.10) 0%, rgba(76,201,255,0.06) 100%)',
                  border: '1px solid rgba(122,92,255,0.20)',
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span
                    className="px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider"
                    style={{ background: 'rgba(122,92,255,0.15)', border: '1px solid rgba(122,92,255,0.25)', color: '#9B7FFF' }}
                  >
                    Issue #{issue.issueNumber}
                  </span>
                  <span
                    className="px-2.5 py-1 rounded-full text-[11px] font-medium"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', color: '#A7A3C4' }}
                  >
                    {issue.category}
                  </span>
                </div>

                <h1 className="font-display font-bold text-[26px] sm:text-[32px] leading-tight mb-3" style={{ color: '#F2F4FF' }}>
                  {issue.title}
                </h1>

                <p className="text-[14px] leading-relaxed mb-4" style={{ color: '#A7A3C4' }}>
                  {issue.excerpt}
                </p>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1.5">
                    <Calendar size={12} style={{ color: '#A7A3C4' }} />
                    <span className="text-[12px]" style={{ color: '#A7A3C4' }}>{issue.date}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock size={12} style={{ color: '#A7A3C4' }} />
                    <span className="text-[12px]" style={{ color: '#A7A3C4' }}>{issue.readTime}</span>
                  </div>
                </div>
              </div>

              {/* Private notice */}
              <div
                className="rounded-xl p-4 mb-8"
                style={{ background: 'rgba(76,201,255,0.04)', border: '1px solid rgba(76,201,255,0.12)' }}
              >
                <p className="text-[13px]" style={{ color: 'rgba(167,163,196,0.80)' }}>
                  🔒 This issue is part of the private Panorama community archive, visible to verified residents only.
                </p>
              </div>

              {/* Body */}
              <div className="space-y-8 mb-12">
                {issue.body.map((section, idx) => (
                  <div key={idx}>
                    {section.heading && (
                      <h2 className="font-display font-semibold text-[19px] mb-3" style={{ color: '#F2F4FF' }}>
                        {section.heading}
                      </h2>
                    )}
                    {section.content && (
                      <p className="text-[15px] leading-relaxed" style={{ color: '#A7A3C4' }}>
                        {section.content}
                      </p>
                    )}
                    {idx < issue.body.length - 1 && (
                      <div className="neon-divider mt-8" />
                    )}
                  </div>
                ))}
              </div>

              {/* Tags */}
              {issue.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-10">
                  {issue.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 rounded-full text-[12px] font-medium"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: '#A7A3C4' }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Related issues */}
              {relatedIssues.length > 0 && (
                <div className="mb-10">
                  <h2 className="font-display font-semibold text-[18px] mb-4" style={{ color: '#F2F4FF' }}>
                    More from the Archive
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {relatedIssues.map((i) => <RelatedIssueCard key={i.id} issue={i} />)}
                  </div>
                </div>
              )}

              {/* Back CTA */}
              <div className="pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <Link href="/newsletter" className="btn-secondary text-[14px] px-5 py-2.5 inline-flex items-center gap-2">
                  <ArrowLeft size={14} /> Back to Newsletter Archive
                </Link>
              </div>
            </div>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
